import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://coupleinapp.com').replace(/\/$/, '');
const DEFAULT_IMAGE = '/couplein_logo.png';

/**
 * Centralised, robust SEO component for all CoupleIn pages.
 * Injects Title, Meta Description, Keywords, Canonical URLs, OpenGraph, Twitter Cards, and Structured Schema JSON-LD.
 */
export const SEO = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd = null,
  keywords = '',
  article = null,
}) => {
  const fullTitle = title?.includes('CoupleIn') ? title : `${title} | CoupleIn`;
  const canonical = `${SITE_URL}${path}`;
  const absImage = image?.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`;

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'}
      />

      {/* OpenGraph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absImage} />
      <meta property="og:site_name" content="CoupleIn; For Unique Couples" />
      <meta property="og:locale" content="en_US" />

      {/* Article Specific OpenGraph */}
      {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />
      <meta name="twitter:site" content="@coupleinapp" />
      <meta name="twitter:creator" content="@coupleinapp" />

      {/* Structured Data JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
